import type {
  ConversationActionState,
  ConversationLifecycleState,
  DurableConversation,
} from "./conversations";

const LIFECYCLES = new Set(["open", "archived"]);
const ACTIONS = ["resume", "stop", "archive", "unarchive", "delete"] as const;
const RUNTIME_STATES = new Set([
  "detached",
  "activating",
  "idle",
  "running",
  "suspended",
]);
const SESSION_STATUSES = new Set([
  "active",
  "idle",
  "suspended",
  "ended",
  "compacted_into",
]);
const AVAILABILITIES = new Set([
  "reuse_segment",
  "create_segment",
  "unavailable",
]);
const EXECUTION_STATUSES = new Set([
  "queued",
  "running",
  "completed",
  "failed",
  "cancelled",
  "deferred",
  "waiting_approval",
  "paused",
]);

function isActionState(value: unknown): value is ConversationActionState {
  if (!value || typeof value !== "object") return false;
  const action = value as Record<string, unknown>;
  return (
    typeof action.allowed === "boolean" &&
    (action.reason === null || typeof action.reason === "string")
  );
}

function isLifecycleState(value: unknown): value is ConversationLifecycleState {
  if (!value || typeof value !== "object") return false;
  const state = value as Record<string, unknown>;
  const runtime = state.runtime as Record<string, unknown> | undefined;
  const execution = state.execution as Record<string, unknown> | null;
  const actions = state.actions as Record<string, unknown> | undefined;
  return Boolean(
    typeof state.lifecycle === "string" &&
    LIFECYCLES.has(state.lifecycle) &&
    typeof state.archived === "boolean" &&
    runtime &&
    typeof runtime.state === "string" &&
    RUNTIME_STATES.has(runtime.state) &&
    Number.isInteger(runtime.generation) &&
    (runtime.runtime_session_status === null ||
      (typeof runtime.runtime_session_status === "string" &&
        SESSION_STATUSES.has(runtime.runtime_session_status))) &&
    typeof runtime.availability === "string" &&
    AVAILABILITIES.has(runtime.availability) &&
    (execution === null ||
      (execution &&
        typeof execution.id === "string" &&
        typeof execution.status === "string" &&
        EXECUTION_STATUSES.has(execution.status) &&
        (execution.error_kind === null ||
          typeof execution.error_kind === "string") &&
        typeof execution.is_active === "boolean")) &&
    actions &&
    ACTIONS.every((action) => isActionState(actions[action])),
  );
}

function failClosedLifecycle(
  conversation: DurableConversation,
): ConversationLifecycleState {
  const blocked = { allowed: false, reason: "invalid_status_projection" };
  return {
    lifecycle: conversation.archived_at ? "archived" : "open",
    archived: conversation.archived_at !== null,
    runtime: {
      state: conversation.runtime?.state ?? "detached",
      generation: conversation.runtime?.generation ?? 0,
      runtime_session_status: null,
      availability: "unavailable",
    },
    execution: null,
    actions: {
      resume: blocked,
      stop: blocked,
      archive: blocked,
      unarchive: blocked,
      delete: blocked,
    },
  };
}

export function normalizeConversation(
  conversation: DurableConversation,
): DurableConversation {
  if (conversation.lifecycle === undefined) return conversation;
  if (isLifecycleState(conversation.lifecycle)) return conversation;
  return { ...conversation, lifecycle: failClosedLifecycle(conversation) };
}
